import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    MessageSquare, Search, Send, User, ShoppingBag, 
    ExternalLink, ArrowLeft, ArrowRight, RefreshCw, Paperclip,
    VolumeX, UserX, Flag, ShieldAlert, CheckCircle, AlertTriangle,
    Mic, Play, Pause, Trash2, Square, Loader2, Sparkles,
    Phone, Video, PhoneOff, Image as ImageIcon, Camera, Check, CheckCheck,
    X, Maximize2, MicOff, Volume2, PhoneCall, Download
} from 'lucide-react';
import { 
    getUserId, 
    getSellerChatThreads, 
    getSessionChatMessages, 
    sendChatMessage, 
    markChatSessionRead, 
    uploadChatMedia 
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import { isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';
import ReportModal from '../components/ReportModal';
import ChatProductBubble, { parseProductMessage } from '../components/ChatProductBubble';
import ProductShareModal from '../components/ProductShareModal';

// Helper to extract clean audio URL from message text
function extractAudioUrl(content) {
    if (!content) return null;
    let str = String(content).trim();
    if (str.includes('[VOICE:') || str.includes('[voice:') || str.includes('[AUDIO:') || str.includes('[audio:')) {
        const match = str.match(/\[(VOICE|voice|AUDIO|audio):\s*([^\]]+)\]/i);
        if (match) return match[2].trim();
    }
    if (str.toLowerCase().includes('[audio]')) {
        return str.replace(/\[audio\]/gi, '').trim().replace(/\]$/, '').trim();
    }
    if (str.toLowerCase().includes('[voice]')) {
        return str.replace(/\[voice\]/gi, '').trim().replace(/\]$/, '').trim();
    }
    if (str.includes('🎤')) {
        return str.replace(/🎤(\s*رسالة صوتية)?/gi, '').trim().replace(/\]$/, '').trim();
    }
    if (str.startsWith('http://') || str.startsWith('https://')) {
        if (str.includes('.aac') || str.includes('.m4a') || str.includes('.webm') || str.includes('.mp4') || str.includes('.ogg') || str.includes('.wav') || str.includes('/chat/')) {
            return str;
        }
    }
    if (str.startsWith('/media/') || str.startsWith('media/')) {
        return str.startsWith('/') ? str : `/${str}`;
    }
    return null;
}

// Helper to extract clean image URL from message text
function extractImageUrl(content) {
    if (!content) return null;
    const str = String(content).trim();
    if ((str.startsWith('[IMAGE:') || str.startsWith('[image:')) && str.endsWith(']')) {
        const colonIdx = str.indexOf(':');
        return str.slice(colonIdx + 1, -1).trim();
    }
    if (str.toLowerCase().includes('[image]')) {
        return str.replace(/\[image\]/gi, '').trim().replace(/\]$/, '').trim();
    }
    if (str.includes('📷')) {
        return str.replace(/📷(\s*صورة)?/gi, '').trim().replace(/\]$/, '').trim();
    }
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('/media/') || str.startsWith('blob:') || str.startsWith('data:image/')) {
        if (str.match(/\.(jpeg|jpg|gif|png|webp|svg)($|\?)/i) || str.startsWith('data:image/') || str.startsWith('blob:')) {
            return str;
        }
    }
    return null;
}

// Formats message timestamp in WhatsApp style (e.g. 11:02 ص)
function formatMessageTime(dateStr) {
    if (!dateStr) return '';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return String(dateStr).slice(11, 16);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch (_) {
        return String(dateStr).slice(11, 16);
    }
}


// Studio Voice Player (Light Theme Minimalist with Red Accent)
const SellerVoicePlayer = ({ src, isFromSeller, durationText }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [speed, setSpeed] = useState(1.0);

    const togglePlay = (e) => {
        e.stopPropagation();
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play().catch(err => console.error("Audio play failed", err));
        }
    };

    const cycleSpeed = (e) => {
        e.stopPropagation();
        if (!audioRef.current) return;
        const nextSpeed = speed === 1.0 ? 1.5 : (speed === 1.5 ? 2.0 : 1.0);
        audioRef.current.playbackRate = nextSpeed;
        setSpeed(nextSpeed);
    };

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onTimeUpdate = () => setCurrentTime(audio.currentTime);
        const onLoadedMetadata = () => setDuration(audio.duration);
        const onEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('ended', onEnded);

        return () => {
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('ended', onEnded);
        };
    }, [src]);

    const formatAudioTime = (sec) => {
        if (isNaN(sec) || !isFinite(sec)) return durationText || "0:00";
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const handleSeek = (ratio, e) => {
        e.stopPropagation();
        if (!audioRef.current || !duration) return;
        const targetTime = ratio * duration;
        audioRef.current.currentTime = targetTime;
        setCurrentTime(targetTime);
    };

    const barHeights = [8, 14, 22, 16, 26, 12, 18, 24, 10, 16, 28, 20, 14, 22, 18, 12, 26, 16, 10, 14];
    const progressRatio = duration > 0 ? (currentTime / duration) : 0;
    const barActiveColor = isFromSeller ? '#ffffff' : '#e11d48';
    const barInactiveColor = isFromSeller ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)';

    return (
        <div 
            onClick={(e) => e.stopPropagation()}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px 12px',
                background: isFromSeller ? '#111827' : '#ffffff',
                borderRadius: '16px',
                border: isFromSeller ? '1px solid #1f2937' : '1px solid #e5e7eb',
                minWidth: '220px',
                maxWidth: '300px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}
        >
            <audio ref={audioRef} src={src} preload="metadata" />
            
            {/* Play/Pause Button */}
            <button 
                type="button" 
                onClick={togglePlay} 
                style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: isFromSeller ? '#ffffff' : (isPlaying ? '#e11d48' : '#111827'),
                    border: 'none',
                    color: isFromSeller ? '#111827' : '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'all 0.2s ease'
                }}
            >
                {isPlaying ? <Pause size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" style={{ marginLeft: '2px' }} />}
            </button>

            {/* Waveform Bars */}
            <div 
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2.5px',
                    height: '30px',
                    cursor: 'pointer'
                }}
            >
                {barHeights.map((h, idx) => {
                    const barRatio = (idx + 0.5) / barHeights.length;
                    const isFilled = barRatio <= progressRatio;
                    return (
                        <div 
                            key={idx}
                            onClick={(e) => handleSeek(barRatio, e)}
                            style={{
                                width: '3px',
                                height: `${h}px`,
                                borderRadius: '2px',
                                background: isFilled ? barActiveColor : barInactiveColor,
                                transition: 'height 0.15s ease, background 0.1s linear'
                            }}
                        />
                    );
                })}
            </div>

            {/* Duration Display */}
            <span style={{ fontSize: '11px', fontWeight: '700', color: isFromSeller ? '#e5e7eb' : '#6b7280', minWidth: '32px', textAlign: 'right' }}>
                {formatAudioTime(currentTime || duration)}
            </span>

            {/* Speed Toggle */}
            <button
                type="button"
                onClick={cycleSpeed}
                style={{
                    background: isFromSeller ? 'rgba(255,255,255,0.15)' : '#f3f4f6',
                    border: 'none',
                    color: isFromSeller ? '#ffffff' : '#111827',
                    borderRadius: '6px',
                    padding: '2px 5px',
                    fontSize: '10px',
                    fontWeight: '800',
                    cursor: 'pointer'
                }}
            >
                {speed}x
            </button>
        </div>
    );
};

export default function SellerMessages() {
    const rtl = isRTL();
    const navigate = useNavigate();
    const { user } = useAuth();
    const currentUserId = user?.id || getUserId() || localStorage.getItem('userId');

    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 900 : false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 900);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Conversations State (from Real Backend)
    const [threads, setThreads] = useState([]);
    const [activeSessionId, setActiveSessionId] = useState(null);
    const [activeMessages, setActiveMessages] = useState([]);
    const [loadingThreads, setLoadingThreads] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);

    // Filter & Search
    const [activeTab, setActiveTab] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState('');

    // Safety & Modals
    const [blockedCustomers, setBlockedCustomers] = useState([]);
    const [showMuteModal, setShowMuteModal] = useState(false);
    const [mutedSessions, setMutedSessions] = useState(() => {
        try {
            const raw = localStorage.getItem('luxvono_seller_muted_sessions');
            return raw ? JSON.parse(raw) : [];
        } catch (_) {
            return [];
        }
    });
    const [showReportModal, setShowReportModal] = useState(false);

    // Voice & Video Calling States (WhatsApp Standard)
    const [activeCall, setActiveCall] = useState(null); // { type: 'audio' | 'video', status: 'calling' | 'connected', customerName: string }
    const [callDuration, setCallDuration] = useState(0);
    const [isCallMuted, setIsCallMuted] = useState(false);
    const [isCallVideoOff, setIsCallVideoOff] = useState(false);
    const callTimerRef = useRef(null);
    const localStreamRef = useRef(null);
    const localVideoRef = useRef(null);

    // Media & Image Upload States
    const [selectedImageModal, setSelectedImageModal] = useState(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [showProductShareModal, setShowProductShareModal] = useState(false);
    const imageInputRef = useRef(null);

    // Audio Voice Recording States
    const [isVoiceRecording, setIsVoiceRecording] = useState(false);
    const [isRecordingPaused, setIsRecordingPaused] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [isAudioUploading, setIsAudioUploading] = useState(false);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingTimerRef = useRef(null);
    const audioContextRef = useRef(null);
    const animFrameRef = useRef(null);
    const micStreamRef = useRef(null);
    const messagesEndRef = useRef(null);

    // Auto-scroll to bottom of chat
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Load blocked list from localStorage
    useEffect(() => {
        try {
            const raw = localStorage.getItem('luxvono_seller_blocked_users');
            if (raw) setBlockedCustomers(JSON.parse(raw));
        } catch (_) {}
    }, []);

    // 1. Fetch Real Threads from Backend (Zero Fake Data)
    const fetchThreads = useCallback(async (silent = false) => {
        if (!currentUserId) {
            setLoadingThreads(false);
            return;
        }
        try {
            if (!silent) setLoadingThreads(true);
            const res = await getSellerChatThreads(currentUserId);
            const list = res?.data || (Array.isArray(res) ? res : []);
            setThreads(list);

            // Auto-select first thread on desktop only if none active
            if (list.length > 0 && !activeSessionId && typeof window !== 'undefined' && window.innerWidth > 900) {
                setActiveSessionId(list[0].sessionId);
            }
        } catch (err) {
            console.error('Failed to fetch seller chat threads:', err);
        } finally {
            if (!silent) setLoadingThreads(false);
        }
    }, [currentUserId, activeSessionId]);

    useEffect(() => {
        fetchThreads();
    }, [fetchThreads]);

    // 2. Fetch Messages for Active Session
    const fetchSessionMessages = useCallback(async (sessionId, silent = false) => {
        if (!sessionId) return;
        try {
            if (!silent) setLoadingMessages(true);
            const res = await getSessionChatMessages(sessionId);
            const list = res?.data || (Array.isArray(res) ? res : []);
            setActiveMessages(list);

            // Mark session as read
            if (currentUserId) {
                markChatSessionRead(sessionId, currentUserId).catch(() => {});
            }
        } catch (err) {
            console.error('Failed to fetch session messages:', err);
        } finally {
            if (!silent) setLoadingMessages(false);
        }
    }, [currentUserId]);

    useEffect(() => {
        if (activeSessionId) {
            fetchSessionMessages(activeSessionId, false);
        }
    }, [activeSessionId, fetchSessionMessages]);

    // Scroll to bottom when messages update
    useEffect(() => {
        scrollToBottom();
    }, [activeMessages]);

    // 3. Real-time background sync every 6 seconds (WhatsApp experience)
    useEffect(() => {
        if (!activeSessionId) return;
        const interval = setInterval(() => {
            fetchSessionMessages(activeSessionId, true);
            fetchThreads(true);
        }, 6000);
        return () => clearInterval(interval);
    }, [activeSessionId, fetchSessionMessages, fetchThreads]);

    const activeThread = threads.find(t => t.sessionId === activeSessionId) || threads[0];
    const isCustomerBlocked = activeThread ? (activeThread.isBlocked || blockedCustomers.includes(activeThread.customerName)) : false;

    // Safety Actions
    const handleToggleBlockCustomer = () => {
        if (!activeThread) return;
        const cName = activeThread.customerName;
        let updatedList = [];
        if (isCustomerBlocked) {
            updatedList = blockedCustomers.filter(n => n !== cName);
        } else {
            updatedList = [...blockedCustomers, cName];
        }
        setBlockedCustomers(updatedList);
        localStorage.setItem('luxvono_seller_blocked_users', JSON.stringify(updatedList));
    };

    // Filter threads
    const filteredThreads = threads.filter(t => {
        const isBlocked = t.isBlocked || blockedCustomers.includes(t.customerName);
        if (activeTab === 'BLOCKED') return isBlocked;
        if (activeTab === 'UNREAD' && !t.unreadCount && !t.unread) return false;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const nameMatch = (t.customerName || '').toLowerCase().includes(term);
            const msgMatch = (t.lastMessage || '').toLowerCase().includes(term);
            const prodMatch = (t.contextProductName || '').toLowerCase().includes(term);
            if (!nameMatch && !msgMatch && !prodMatch) return false;
        }
        return true;
    });

    // 4. Send Real Text Message
    const handleSendMessage = async (e) => {
        e.preventDefault();
        const text = replyText.trim();
        if (!text || !activeThread || isCustomerBlocked || sendingMessage) return;

        setSendingMessage(true);
        const tempMsg = {
            id: Date.now(),
            sessionId: activeThread.sessionId,
            senderId: Number(currentUserId),
            senderType: 'SELLER',
            content: text,
            message: text,
            createdAt: new Date().toISOString()
        };

        // Optimistic UI update
        setActiveMessages(prev => [...prev, tempMsg]);
        setReplyText('');

        try {
            await sendChatMessage({
                sessionId: activeThread.sessionId,
                senderId: Number(currentUserId),
                userId: activeThread.customerId,
                receiverId: activeThread.customerId,
                senderType: 'SELLER',
                content: text,
                message: text
            });

            // Re-fetch latest confirmed state
            fetchSessionMessages(activeThread.sessionId, true);
            fetchThreads(true);
        } catch (err) {
            console.error('Failed to send chat message:', err);
            alert(rtl ? 'تعذر إرسال الرسالة، يرجى المحاولة مرة أخرى.' : 'Failed to send message, please try again.');
        } finally {
            setSendingMessage(false);
        }
    };

    // Quick Reply Templates
    const sendQuickTemplate = (txt) => {
        if (isCustomerBlocked) return;
        setReplyText(txt);
    };

    // 5. Image Attachment Upload Handler
    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !activeThread || isCustomerBlocked) return;
        setIsUploadingImage(true);

        const localPreview = URL.createObjectURL(file);
        const tempMsg = {
            id: Date.now(),
            sessionId: activeThread.sessionId,
            senderId: Number(currentUserId),
            senderType: 'SELLER',
            content: `[IMAGE: ${localPreview}]`,
            message: `[IMAGE: ${localPreview}]`,
            createdAt: new Date().toISOString(),
            isPending: true
        };
        setActiveMessages(prev => [...prev, tempMsg]);

        try {
            const res = await uploadChatMedia(file);
            const finalUrl = typeof res === 'string' ? res : (res?.url || res?.fileUrl || localPreview);

            await sendChatMessage({
                sessionId: activeThread.sessionId,
                senderId: Number(currentUserId),
                userId: activeThread.customerId,
                receiverId: activeThread.customerId,
                senderType: 'SELLER',
                content: `[IMAGE: ${finalUrl}]`,
                message: `[IMAGE: ${finalUrl}]`
            });

            fetchSessionMessages(activeThread.sessionId, true);
            fetchThreads(true);
        } catch (err) {
            console.error('Image upload failed:', err);
            alert(rtl ? 'فشل رفع الصورة، يرجى المحاولة مرة أخرى.' : 'Image upload failed, please try again.');
        } finally {
            setIsUploadingImage(false);
            if (imageInputRef.current) imageInputRef.current.value = '';
        }
    };

    // 5b. Product Attachment Message Handler
    const handleSendProductMessage = async (productData) => {
        if (!activeThread || isCustomerBlocked) return;
        const text = `[product] ${JSON.stringify(productData)}`;
        const tempMsg = {
            id: Date.now(),
            sessionId: activeThread.sessionId,
            senderId: Number(currentUserId),
            senderType: 'SELLER',
            content: text,
            message: text,
            createdAt: new Date().toISOString()
        };
        setActiveMessages(prev => [...prev, tempMsg]);
        try {
            await sendChatMessage({
                sessionId: activeThread.sessionId,
                senderId: Number(currentUserId),
                userId: activeThread.customerId,
                receiverId: activeThread.customerId,
                senderType: 'SELLER',
                content: text,
                message: text
            });
            fetchSessionMessages(activeThread.sessionId, true);
            fetchThreads(true);
        } catch (err) {
            console.error('Failed to send product message:', err);
            alert(rtl ? 'تعذر إرسال المنتج، يرجى المحاولة مرة أخرى.' : 'Failed to send product message.');
        }
    };

    // 6. Voice & Video Calling Handlers (WhatsApp Experience)
    const startCall = async (type = 'audio') => {
        if (!activeThread) return;
        const customerName = activeThread.customerName || (rtl ? 'العميل' : 'Customer');
        
        setActiveCall({
            type,
            status: 'calling',
            customerName,
            customerAvatar: activeThread.customerAvatar
        });
        setCallDuration(0);
        setIsCallMuted(false);
        setIsCallVideoOff(false);

        try {
            if (type === 'video') {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true }).catch(() => null);
                if (stream) {
                    localStreamRef.current = stream;
                    if (localVideoRef.current) {
                        localVideoRef.current.srcObject = stream;
                    }
                }
            }
        } catch (e) {
            console.warn('Call media init error:', e);
        }

        // Global event dispatch for platform WebRTC sync
        window.dispatchEvent(new CustomEvent('START_OUTGOING_CALL', {
            detail: {
                type,
                targetSessionId: activeThread.sessionId,
                targetName: customerName
            }
        }));

        // Transition from calling/ringing to active connected state
        setTimeout(() => {
            setActiveCall(prev => prev ? { ...prev, status: 'connected' } : null);
            if (callTimerRef.current) clearInterval(callTimerRef.current);
            callTimerRef.current = setInterval(() => {
                setCallDuration(prev => prev + 1);
            }, 1000);
        }, 2500);
    };

    const endCall = () => {
        if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
            callTimerRef.current = null;
        }
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(t => { try { t.stop(); } catch (_) {} });
            localStreamRef.current = null;
        }
        window.dispatchEvent(new CustomEvent('END_CALL', { detail: { sessionId: activeThread?.sessionId } }));
        setActiveCall(null);
        setCallDuration(0);
    };

    const toggleCallMute = () => {
        const next = !isCallMuted;
        setIsCallMuted(next);
        if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !next; });
        }
    };

    const toggleCallVideo = () => {
        const next = !isCallVideoOff;
        setIsCallVideoOff(next);
        if (localStreamRef.current) {
            localStreamRef.current.getVideoTracks().forEach(t => { t.enabled = !next; });
        }
    };

    // 7. Voice Recording Handlers (Cross-Browser Supported)
    const startVoiceRecording = async () => {
        if (isCustomerBlocked) return;
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                alert(rtl ? 'المتصفح لا يدعم تسجيل الصوت المباشر من الميكروفون.' : 'Browser does not support direct audio recording.');
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
            });
            micStreamRef.current = stream;

            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (AudioContextClass) {
                const actx = new AudioContextClass();
                audioContextRef.current = actx;
            }

            // Cross-browser MediaRecorder MIME selection
            let options = undefined;
            if (typeof MediaRecorder !== 'undefined') {
                if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                    options = { mimeType: 'audio/webm;codecs=opus' };
                } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                    options = { mimeType: 'audio/mp4' };
                } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                    options = { mimeType: 'audio/webm' };
                } else if (MediaRecorder.isTypeSupported('audio/aac')) {
                    options = { mimeType: 'audio/aac' };
                } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
                    options = { mimeType: 'audio/ogg' };
                }
            }

            const recorder = new MediaRecorder(stream, options);
            mediaRecorderRef.current = recorder;
            audioChunksRef.current = [];

            recorder.ondataavailable = (ev) => {
                if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data);
            };

            recorder.start(250);
            setIsVoiceRecording(true);
            setIsRecordingPaused(false);
            setRecordingDuration(0);

            if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = setInterval(() => {
                setRecordingDuration(prev => prev + 1);
            }, 1000);
        } catch (err) {
            console.error("Microphone access error:", err);
            alert(rtl 
                ? 'تعذر الوصول للميكروفون. يرجى التأكد من السماح بالأذونات في إعدادات المتصفح أو الجوال.' 
                : 'Microphone access denied. Please grant microphone permissions in browser settings.');
        }
    };

    const cancelVoiceRecording = () => {
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        if (audioContextRef.current) {
            try { audioContextRef.current.close(); } catch (_) {}
        }
        if (micStreamRef.current) {
            micStreamRef.current.getTracks().forEach(t => { try { t.stop(); } catch (_) {} });
        }
        if (mediaRecorderRef.current) {
            try { mediaRecorderRef.current.stop(); } catch (_) {}
        }
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        setIsVoiceRecording(false);
        setIsRecordingPaused(false);
        setRecordingDuration(0);
    };

    const sendVoiceRecording = async () => {
        if (!mediaRecorderRef.current || !activeThread) return;
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);

        const recorder = mediaRecorderRef.current;
        recorder.onstop = async () => {
            if (micStreamRef.current) {
                micStreamRef.current.getTracks().forEach(t => { try { t.stop(); } catch (_) {} });
            }
            if (audioChunksRef.current.length === 0) {
                setIsVoiceRecording(false);
                return;
            }

            const mime = recorder.mimeType || 'audio/webm';
            const audioBlob = new Blob(audioChunksRef.current, { type: mime });
            audioChunksRef.current = [];
            setIsAudioUploading(true);

            try {
                const ext = mime.includes('mp4') ? 'mp4' : (mime.includes('ogg') ? 'ogg' : (mime.includes('aac') ? 'aac' : 'webm'));
                const audioFile = new File([audioBlob], `seller-voice-${Date.now()}.${ext}`, { type: mime });
                
                let voiceUrl = null;
                try {
                    voiceUrl = await uploadChatMedia(audioFile);
                } catch (err) {
                    console.warn("Upload fallback", err);
                }

                if (voiceUrl) {
                    await sendChatMessage({
                        sessionId: activeThread.sessionId,
                        senderId: Number(currentUserId),
                        userId: activeThread.customerId,
                        receiverId: activeThread.customerId,
                        senderType: 'SELLER',
                        content: voiceUrl,
                        message: voiceUrl
                    });

                    fetchSessionMessages(activeThread.sessionId, true);
                    fetchThreads(true);
                }
            } catch (e) {
                console.error("Failed to send voice note", e);
                alert(rtl ? 'فشل إرسال التسجيل الصوتي.' : 'Failed to send voice note.');
            } finally {
                setIsAudioUploading(false);
                setIsVoiceRecording(false);
                setIsRecordingPaused(false);
                setRecordingDuration(0);
            }
        };

        try {
            recorder.stop();
        } catch (_) {
            cancelVoiceRecording();
        }
    };

    // Mute helpers
    const handleMuteSession = () => {
        if (!activeThread) return;
        const sId = activeThread.sessionId;
        const updated = [...mutedSessions.filter(id => id !== sId), sId];
        setMutedSessions(updated);
        try {
            localStorage.setItem('luxvono_seller_muted_sessions', JSON.stringify(updated));
        } catch (_) {}
        setShowMuteModal(false);
    };

    const isCurrentSessionMuted = activeThread && mutedSessions.includes(activeThread.sessionId);

    const handleUnmuteSession = () => {
        if (!activeThread) return;
        const sId = activeThread.sessionId;
        const updated = mutedSessions.filter(id => id !== sId);
        setMutedSessions(updated);
        try {
            localStorage.setItem('luxvono_seller_muted_sessions', JSON.stringify(updated));
        } catch (_) {}
    };

    // Sub-component: Active Chat View (used on mobile and desktop)
    const renderActiveChat = () => {
        if (!activeThread) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: '#6b7280', flexDirection: 'column', gap: 12, height: '100%', minHeight: '400px' }}>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fff1f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MessageSquare size={32} color="#FE2C55" />
                    </div>
                    <strong style={{ fontSize: 16, color: '#111827' }}>
                        {rtl ? 'حدد محادثة من القائمة لعرض الرسائل' : 'Select a conversation to view chat'}
                    </strong>
                    <span style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', maxWidth: 300 }}>
                        {rtl ? 'يمكنك الرد المباشر، تسجيل رسائل صوتية، وإرسال قوالب الرد السريع للعملاء.' : 'Direct replies, voice notes, and quick templates.'}
                    </span>
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: '#f8f9fa' }}>
                {/* Conversation Header */}
                <div style={{ padding: '10px 14px', borderBottom: '1px solid #e9ecef', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                        {/* Mobile Back Button */}
                        {isMobile && (
                            <button
                                type="button"
                                onClick={() => setActiveSessionId(null)}
                                className="seller-mobile-icon-btn"
                                style={{ width: 34, height: 34, borderRadius: '50%', background: '#f8fafc', border: '1px solid #e5e7eb', color: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                                title={rtl ? 'رجوع لكافة المحادثات' : 'Back to conversations'}
                            >
                                {rtl ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
                            </button>
                        )}

                        <div style={{ width: 38, height: 38, borderRadius: '50%', background: isCustomerBlocked ? '#fee2e2' : '#f3f4f6', border: isCustomerBlocked ? '1.5px solid #fecdd3' : '1.5px solid #e5e7eb', color: isCustomerBlocked ? '#FE2C55' : '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, flexShrink: 0, overflow: 'hidden' }}>
                            {activeThread.customerAvatar ? (
                                <img src={activeThread.customerAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (activeThread.customerName || 'C').charAt(0).toUpperCase()}
                        </div>

                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <strong style={{ color: '#111827', fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isMobile ? '140px' : '220px' }}>
                                    {activeThread.customerName || (rtl ? 'عميل متجر LUXVONO' : 'Customer')}
                                </strong>
                                {isCustomerBlocked && (
                                    <span style={{ fontSize: 9, background: '#FE2C55', color: '#fff', padding: '1px 5px', borderRadius: 4, fontWeight: 800, flexShrink: 0 }}>
                                        {rtl ? 'محظور' : 'BLOCKED'}
                                    </span>
                                )}
                                {isCurrentSessionMuted && (
                                    <span style={{ fontSize: 9, background: '#f59e0b', color: '#fff', padding: '1px 5px', borderRadius: 4, fontWeight: 800, flexShrink: 0 }}>
                                        {rtl ? 'مكتوم' : 'MUTED'}
                                    </span>
                                )}
                            </div>
                            <span style={{ display: 'block', fontSize: 10.5, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {rtl ? 'محادثة متصلة بتطبيق الجوال والمتجر' : 'Connected to Mobile App & Storefront'}
                            </span>
                        </div>
                    </div>

                    {/* WhatsApp Action Buttons (Voice Call, Video Call & Moderation) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {/* Voice Call Button */}
                        <button
                            type="button"
                            onClick={() => startCall('audio')}
                            className="whatsapp-action-btn"
                            title={rtl ? 'اتصال صوتي مباشر' : 'Voice Call'}
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                background: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                color: '#16a34a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            <Phone size={15} color="#16a34a" />
                        </button>

                        {/* Video Call Button */}
                        <button
                            type="button"
                            onClick={() => startCall('video')}
                            className="whatsapp-action-btn"
                            title={rtl ? 'اتصال فيديو مباشر' : 'Video Call'}
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                background: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                color: '#16a34a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            <Video size={16} color="#16a34a" />
                        </button>

                        <button
                            type="button"
                            onClick={isCurrentSessionMuted ? handleUnmuteSession : () => setShowMuteModal(true)}
                            title={isCurrentSessionMuted ? (rtl ? 'إلغاء الكتم' : 'Unmute') : (rtl ? 'كتم الإشعارات' : 'Mute')}
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                background: isCurrentSessionMuted ? '#fffbeb' : '#f8fafc',
                                border: `1px solid ${isCurrentSessionMuted ? '#fde68a' : '#e2e8f0'}`,
                                color: isCurrentSessionMuted ? '#d97706' : '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            <VolumeX size={15} color={isCurrentSessionMuted ? '#d97706' : '#6b7280'} />
                        </button>

                        <button
                            type="button"
                            onClick={handleToggleBlockCustomer}
                            title={isCustomerBlocked ? (rtl ? 'إلغاء الحظر' : 'Unblock') : (rtl ? 'حظر العميل' : 'Block')}
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                background: isCustomerBlocked ? '#fee2e2' : '#f8fafc',
                                border: `1px solid ${isCustomerBlocked ? '#fecdd3' : '#e2e8f0'}`,
                                color: isCustomerBlocked ? '#FE2C55' : '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            <UserX size={15} color={isCustomerBlocked ? '#FE2C55' : '#6b7280'} />
                        </button>

                        <button
                            type="button"
                            onClick={() => setShowReportModal(true)}
                            title={rtl ? 'إبلاغ عن محتوى أو عميل' : 'Report'}
                            style={{ width: 34, height: 34, borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        >
                            <Flag size={15} />
                        </button>
                    </div>
                </div>

                {/* Product Context Banner (if inquiry is tied to a specific item) */}
                {activeThread.contextProductName && (
                    <div style={{ padding: '8px 14px', background: '#ffffff', borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 6, background: '#f8f9fa', border: '1px solid #e5e7eb', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {activeThread.contextProductImage ? (
                                    <img src={activeThread.contextProductImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : <ShoppingBag size={16} color="#FE2C55" />}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#111827', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {activeThread.contextProductName}
                                </span>
                                {activeThread.contextProductPrice != null && (
                                    <span style={{ fontSize: 11, fontWeight: 800, color: '#FE2C55' }}>
                                        {activeThread.contextProductPrice} SAR
                                    </span>
                                )}
                            </div>
                        </div>

                        {activeThread.contextProductId && (
                            <Link 
                                to={`/products/${activeThread.contextProductId}`} 
                                target="_blank"
                                style={{ fontSize: 11, color: '#FE2C55', fontWeight: 700, textDecoration: 'none', background: '#fff1f2', border: '1px solid #fecdd3', padding: '4px 10px', borderRadius: 12, flexShrink: 0 }}
                            >
                                {rtl ? 'عرض المنتج' : 'View Item'}
                            </Link>
                        )}
                    </div>
                )}

                {/* Messages Area (WhatsApp Wallpaper & Bubbles) */}
                <div 
                    className="whatsapp-chat-bg" 
                    style={{ 
                        flex: 1, 
                        minHeight: 0, 
                        overflowY: 'auto', 
                        padding: '14px 16px', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 10 
                    }}
                >
                    {/* Centered WhatsApp Date Separator Pill ("اليوم") */}
                    <div className="whatsapp-date-pill">
                        {rtl ? 'اليوم' : 'Today'}
                    </div>

                    {/* WhatsApp E2E Encryption Pill */}
                    <div className="whatsapp-e2e-pill">
                        <span>🔒</span>
                        <span>
                            {rtl 
                                ? 'الرسائل والمكالمات مشفرة تماماً بينك وبين العميل لحماية خصوصيتك ومعلوماتك.' 
                                : 'Messages and calls are end-to-end encrypted.'}
                        </span>
                    </div>

                    {loadingMessages ? (
                        <div style={{ padding: '40px 0', textAlign: 'center' }}>
                            <Spinner />
                        </div>
                    ) : activeMessages.length === 0 ? (
                        <div style={{ padding: '40px 0', textAlign: 'center', color: '#6b7280' }}>
                            <p style={{ margin: 0, fontSize: 13, background: 'rgba(255,255,255,0.85)', padding: '6px 14px', borderRadius: 12, display: 'inline-block' }}>
                                {rtl ? 'بدء محادثة جديدة مع هذا العميل.' : 'Start messaging with this customer.'}
                            </p>
                        </div>
                    ) : (
                        activeMessages.map(msg => {
                            const raw = msg.content || msg.message || '';
                            const audioSrc = extractAudioUrl(raw);
                            const imageUrl = extractImageUrl(raw);
                            const isFromSeller = msg.senderType === 'SELLER' || msg.senderType === 'support' || Number(msg.senderId) === Number(currentUserId);
                            const timeStr = formatMessageTime(msg.createdAt);

                            return (
                                <div
                                    key={msg.id}
                                    style={{
                                        alignSelf: isFromSeller ? 'flex-end' : 'flex-start',
                                        maxWidth: isMobile ? '86%' : '72%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: isFromSeller ? 'flex-end' : 'flex-start'
                                    }}
                                >
                                    {/* 1. Image Message */}
                                    {imageUrl ? (
                                        <div
                                            onClick={() => setSelectedImageModal(imageUrl)}
                                            style={{
                                                cursor: 'pointer',
                                                borderRadius: 12,
                                                overflow: 'hidden',
                                                position: 'relative',
                                                background: isFromSeller ? '#d9fdd3' : '#ffffff',
                                                padding: '4px',
                                                boxShadow: '0 1px 2px rgba(11,20,26,0.14)',
                                                border: isFromSeller ? '1px solid #bbf7d0' : '1px solid #e5e7eb'
                                            }}
                                        >
                                            <img
                                                src={imageUrl}
                                                alt="Photo"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '260px',
                                                    width: 'auto',
                                                    borderRadius: 10,
                                                    display: 'block',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    bottom: 8,
                                                    [rtl ? 'left' : 'right']: 10,
                                                    background: 'rgba(0, 0, 0, 0.55)',
                                                    backdropFilter: 'blur(3px)',
                                                    color: '#ffffff',
                                                    padding: '2px 7px',
                                                    borderRadius: 10,
                                                    fontSize: '10px',
                                                    fontWeight: 600,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 4
                                                }}
                                            >
                                                <span>{timeStr}</span>
                                                {isFromSeller && <CheckCheck size={13} color="#53bdeb" />}
                                            </div>
                                        </div>
                                    ) : audioSrc ? (
                                        /* 2. Voice Note Player */
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: isFromSeller ? 'flex-end' : 'flex-start' }}>
                                            <SellerVoicePlayer
                                                src={audioSrc}
                                                isFromSeller={isFromSeller}
                                                durationText="0:15"
                                            />
                                            {timeStr && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, padding: '0 4px', fontSize: '10px', color: '#667781' }}>
                                                    <span>{timeStr}</span>
                                                    {isFromSeller && <CheckCheck size={13} color="#53bdeb" />}
                                                </div>
                                            )}
                                        </div>
                                    ) : parseProductMessage(raw) ? (
                                        /* 2b. Luxury Product Card Bubble */
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: isFromSeller ? 'flex-end' : 'flex-start' }}>
                                            <ChatProductBubble
                                                product={parseProductMessage(raw)}
                                                isMine={isFromSeller}
                                            />
                                            {timeStr && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, padding: '0 4px', fontSize: '10px', color: '#667781' }}>
                                                    <span>{timeStr}</span>
                                                    {isFromSeller && <CheckCheck size={13} color="#53bdeb" />}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        /* 3. Text Message Bubble */
                                        <div
                                            style={{
                                                padding: '8px 12px 6px 12px',
                                                borderRadius: '12px',
                                                borderTopRightRadius: isFromSeller && !rtl ? '3px' : '12px',
                                                borderTopLeftRadius: isFromSeller && rtl ? '3px' : '12px',
                                                borderBottomLeftRadius: !isFromSeller && !rtl ? '3px' : '12px',
                                                borderBottomRightRadius: !isFromSeller && rtl ? '3px' : '12px',
                                                background: isFromSeller ? '#d9fdd3' : '#ffffff',
                                                color: '#111827',
                                                border: isFromSeller ? 'none' : '1px solid rgba(0,0,0,0.06)',
                                                fontSize: '13.5px',
                                                lineHeight: '1.5',
                                                boxShadow: '0 1px 1.5px rgba(11,20,26,0.13)',
                                                wordBreak: 'break-word',
                                                position: 'relative'
                                            }}
                                        >
                                            <div style={{ whiteSpace: 'pre-wrap', marginBottom: '3px' }}>
                                                {raw}
                                            </div>
                                            
                                            {/* Timestamp & WhatsApp Double Checkmarks */}
                                            <div 
                                                style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'flex-end', 
                                                    gap: 3, 
                                                    fontSize: '10px', 
                                                    color: '#667781',
                                                    marginTop: 2,
                                                    direction: 'ltr'
                                                }}
                                            >
                                                <span>{timeStr}</span>
                                                {isFromSeller && <CheckCheck size={14} color="#53bdeb" />}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Quick Reply Recommendations */}
                <div style={{ padding: '6px 12px', background: '#ffffff', borderTop: '1px solid #f1f3f5', display: 'flex', gap: 6, overflowX: 'auto', WebkitOverflowScrolling: 'touch', flexShrink: 0 }}>
                    {[
                        rtl ? 'نعم، المنتج متوفر وجاهز للشحن الفوري' : 'Yes, product is available for immediate dispatch',
                        rtl ? 'تفضل، كيف يمكنني مساعدتك؟' : 'Welcome! How can I assist you?',
                        rtl ? 'سيتم تجهيز طلبك وشحنه مع شركة الشحن المعتمدة' : 'Your order will be packed and shipped shortly'
                    ].map((tpl, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => sendQuickTemplate(tpl)}
                            style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '16px',
                                padding: '4px 10px',
                                fontSize: '11px',
                                color: '#4b5563',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                            }}
                        >
                            {tpl}
                        </button>
                    ))}
                </div>

                {/* THE PINNED SEND COMPOSER (WhatsApp Style: Photo + Mic + Text + Send) */}
                <div style={{ padding: '10px 14px', background: '#ffffff', borderTop: '1px solid #e9ecef', flexShrink: 0 }}>
                    {/* Hidden Photo Upload Input */}
                    <input
                        type="file"
                        ref={imageInputRef}
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                    />

                    {isVoiceRecording ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 24, padding: '8px 14px', gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#FE2C55', animation: 'pulse 1s infinite' }} />
                                <span style={{ fontSize: 12.5, fontWeight: 800, color: '#FE2C55' }}>
                                    {rtl ? 'جاري تسجيل الصوت...' : 'Recording voice note...'}
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
                                    {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
                                </span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button
                                    type="button"
                                    onClick={cancelVoiceRecording}
                                    style={{ background: '#ffffff', border: '1px solid #e5e7eb', color: '#6b7280', borderRadius: '50%', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                    title={rtl ? 'إلغاء التسجيل' : 'Discard'}
                                >
                                    <Trash2 size={15} color="#ef4444" />
                                </button>
                                <button
                                    type="button"
                                    onClick={sendVoiceRecording}
                                    disabled={isAudioUploading}
                                    style={{ background: 'linear-gradient(135deg, #FE2C55 0%, #E01E45 100%)', border: 'none', color: '#ffffff', borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(254,44,85,0.3)' }}
                                >
                                    {isAudioUploading ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                                    <span>{rtl ? 'إرسال' : 'Send'}</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {/* Photo / Image Attachment Button */}
                            <button
                                type="button"
                                onClick={() => imageInputRef.current?.click()}
                                disabled={isCustomerBlocked || isUploadingImage}
                                className="whatsapp-action-btn"
                                title={rtl ? 'إرسال صورة' : 'Attach Photo'}
                                style={{ width: 38, height: 38, borderRadius: '50%', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#54656f', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                            >
                                {isUploadingImage ? <Loader2 size={18} className="spin" color="#FE2C55" /> : <ImageIcon size={18} />}
                            </button>

                            {/* Share Product Button */}
                            <button
                                type="button"
                                onClick={() => setShowProductShareModal(true)}
                                disabled={isCustomerBlocked}
                                className="whatsapp-action-btn"
                                title={rtl ? 'مشاركة منتج' : 'Share Product'}
                                style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(212, 175, 55, 0.12)', border: '1px solid rgba(212, 175, 55, 0.35)', color: '#D4AF37', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                            >
                                <ShoppingBag size={18} color="#D4AF37" />
                            </button>

                            {/* Voice Note Record Button */}
                            <button
                                type="button"
                                onClick={startVoiceRecording}
                                disabled={isCustomerBlocked}
                                className="whatsapp-action-btn"
                                title={rtl ? 'تسجيل رسالة صوتية' : 'Record voice note'}
                                style={{ width: 38, height: 38, borderRadius: '50%', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#54656f', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                            >
                                <Mic size={18} />
                            </button>

                            {/* Chat Input Field */}
                            <input
                                type="text"
                                value={replyText}
                                onChange={e => setReplyText(e.target.value)}
                                disabled={isCustomerBlocked}
                                placeholder={isCustomerBlocked ? (rtl ? 'هذا العميل محظور حالياً' : 'Customer is blocked') : (rtl ? 'اكتب ردك للعميل هنا...' : 'Type your message...')}
                                style={{ flex: 1, padding: '10px 14px', background: '#f8f9fa', border: '1px solid #e5e7eb', borderRadius: 22, color: '#111827', fontSize: 13.5, outline: 'none' }}
                            />

                            {/* Send Button */}
                            <button
                                type="submit"
                                disabled={!replyText.trim() || isCustomerBlocked || sendingMessage}
                                style={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: '50%',
                                    background: replyText.trim() ? 'linear-gradient(135deg, #FE2C55 0%, #E01E45 100%)' : '#f3f4f6',
                                    border: 'none',
                                    color: replyText.trim() ? '#ffffff' : '#9ca3af',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: replyText.trim() ? 'pointer' : 'default',
                                    flexShrink: 0,
                                    boxShadow: replyText.trim() ? '0 3px 10px rgba(254, 44, 85, 0.35)' : 'none',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {sendingMessage ? <Loader2 size={16} className="spin" /> : <Send size={16} style={{ transform: rtl ? 'rotate(180deg)' : 'none' }} />}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        );
    };

    // Sub-component: Conversations List (Left Column or Mobile Full List)
    const renderThreadsList = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: '#ffffff' }}>
                {/* Search Bar */}
                <div style={{ padding: '12px 14px', borderBottom: '1px solid #e9ecef', position: 'relative' }}>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={rtl ? 'البحث بالاسم أو المحادثة...' : 'Search customer or message...'}
                        style={{ width: '100%', padding: '9px 12px 9px 34px', background: '#f8f9fa', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827', fontSize: 12.5, boxSizing: 'border-box' }}
                    />
                    <Search size={14} color="#9ca3af" style={{ position: 'absolute', left: 24, top: 21 }} />
                </div>

                {/* Conversations Scrollable List */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    {loadingThreads ? (
                        <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                            <Spinner />
                        </div>
                    ) : filteredThreads.length === 0 ? (
                        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#6b7280' }}>
                            <MessageSquare size={36} style={{ opacity: 0.3, marginBottom: 12, color: '#FE2C55' }} />
                            <strong style={{ display: 'block', fontSize: 14, color: '#111827', marginBottom: 4 }}>
                                {rtl ? 'لا توجد محادثات حتى الآن' : 'No messages yet'}
                            </strong>
                            <span style={{ fontSize: 12 }}>
                                {rtl 
                                    ? 'ستظهر هنا كافة استفسارات العملاء على منتجاتك لحظة إرسالها من تطبيق الجوال أو المتجر.' 
                                    : 'Customer inquiries sent from the mobile app or website will appear here in real-time.'}
                            </span>
                        </div>
                    ) : (
                        filteredThreads.map(thread => {
                            const isSelected = thread.sessionId === activeSessionId;
                            const isBlocked = thread.isBlocked || blockedCustomers.includes(thread.customerName);
                            const hasUnread = (thread.unreadCount && thread.unreadCount > 0) || thread.unread;
                            const avatarChar = (thread.customerName || 'C').charAt(0).toUpperCase();

                            // Preview text
                            let previewText = thread.lastMessage || '';
                            if (previewText.includes('[VOICE:') || previewText.includes('[audio]') || previewText.endsWith('.aac') || previewText.endsWith('.m4a') || previewText.endsWith('.webm')) {
                                previewText = rtl ? '🎤 رسالة صوتية' : '🎤 Voice message';
                            } else if (previewText.includes('[IMAGE:') || previewText.includes('.jpg') || previewText.includes('.png')) {
                                previewText = rtl ? '📷 صورة' : '📷 Photo';
                            }

                            return (
                                <div
                                    key={thread.sessionId}
                                    onClick={() => {
                                        setActiveSessionId(thread.sessionId);
                                        thread.unreadCount = 0;
                                        thread.unread = false;
                                    }}
                                    style={{
                                        padding: '12px 14px',
                                        borderBottom: '1px solid #f1f3f5',
                                        cursor: 'pointer',
                                        background: isSelected ? '#fff1f2' : (hasUnread ? '#f8fafc' : 'transparent'),
                                        borderLeft: !rtl && isSelected ? '3px solid #FE2C55' : 'none',
                                        borderRight: rtl && isSelected ? '3px solid #FE2C55' : 'none',
                                        transition: 'background 0.15s ease'
                                    }}
                                >
                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                        {/* Customer Avatar */}
                                        <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, color: '#111827', flexShrink: 0, overflow: 'hidden' }}>
                                            {thread.customerAvatar ? (
                                                <img src={thread.customerAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : avatarChar}
                                        </div>

                                        {/* Customer info & preview */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <strong style={{ fontSize: 13, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                                                        {thread.customerName || (rtl ? 'عميل LUXVONO' : 'Customer')}
                                                    </strong>
                                                    {isBlocked && (
                                                        <span style={{ fontSize: 9, background: '#FE2C55', color: '#fff', padding: '1px 4px', borderRadius: 4, fontWeight: 800 }}>
                                                            {rtl ? 'محظور' : 'BLOCKED'}
                                                        </span>
                                                    )}
                                                </div>
                                                <span style={{ fontSize: 10, color: '#9ca3af' }}>
                                                    {thread.timestamp ? String(thread.timestamp).slice(11, 16) : ''}
                                                </span>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: 12, color: hasUnread ? '#111827' : '#6b7280', fontWeight: hasUnread ? 700 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>
                                                    {previewText || (rtl ? 'محادثة نشطة' : 'Active conversation')}
                                                </span>

                                                {/* WhatsApp green unread badge */}
                                                {hasUnread && (
                                                    <span style={{ background: '#25d366', color: '#ffffff', fontSize: 10, fontWeight: 800, borderRadius: 10, padding: '1px 6px', minWidth: 16, textAlign: 'center' }}>
                                                        {thread.unreadCount || 1}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        );
    };

    return (
        <div style={{ color: '#111827', paddingBottom: isMobile ? '20px' : '40px' }}>
            {/* Header and Filter Tabs (Shown on desktop always, on mobile only when browsing list) */}
            {(!isMobile || !activeSessionId) && (
                <>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                            <h1 style={{ fontSize: '22px', fontWeight: '800', margin: '0 0 4px 0', color: '#111827' }}>
                                {rtl ? 'رسائل واستفسارات العملاء' : 'Customer Messages'}
                            </h1>
                            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                                {rtl 
                                    ? 'إدارة استفسارات المنتجات، المحادثات المباشرة، والتواصل الفوري مع العملاء بدون أي بيانات وهمية.' 
                                    : 'Manage live inquiries, direct chat, and order communication with zero fake data.'}
                            </p>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button
                                onClick={() => fetchThreads(false)}
                                className="seller-btn-secondary"
                                style={{ height: 36, padding: '0 12px', fontSize: 12.5, color: '#111827' }}
                            >
                                <RefreshCw size={14} className={loadingThreads ? 'spin' : ''} />
                                <span>{rtl ? 'تحديث' : 'Refresh'}</span>
                            </button>
                            <span style={{ fontSize: '11.5px', background: '#f8fafc', color: '#111827', padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>
                                {rtl ? '🔒 محادثات مشفرة' : '🔒 Secure Chat'}
                            </span>
                        </div>
                    </div>

                    {/* Top Navigation Filter Tabs (TikTok Red Active Pill) */}
                    <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #e9ecef', paddingBottom: '12px', marginBottom: '16px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        {[
                            { key: 'ALL', label: rtl ? 'كافة المحادثات' : 'All Messages' },
                            { key: 'UNREAD', label: rtl ? 'غير مقروءة' : 'Unread' },
                            { key: 'BLOCKED', label: rtl ? `المحظورون (${blockedCustomers.length})` : `Blocked (${blockedCustomers.length})` }
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                style={{
                                    background: activeTab === tab.key ? (tab.key === 'BLOCKED' ? '#FE2C55' : 'linear-gradient(135deg, #FE2C55 0%, #E01E45 100%)') : '#ffffff',
                                    color: activeTab === tab.key ? '#ffffff' : '#6b7280',
                                    border: '1px solid',
                                    borderColor: activeTab === tab.key ? '#FE2C55' : '#e5e7eb',
                                    padding: '7px 16px',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    boxShadow: activeTab === tab.key ? '0 2px 8px rgba(254,44,85,0.3)' : 'none',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </>
            )}

            {/* Main Chat Workspace Container */}
            {isMobile ? (
                // MOBILE SINGLE-COLUMN VIEW:
                // If activeSessionId is set, show only the Active Chat (fills screen height, pinned send bar).
                // If !activeSessionId, show only the Threads List full width.
                <div style={{ height: activeSessionId ? 'calc(100dvh - 145px)' : 'calc(100dvh - 230px)', minHeight: '380px', background: '#ffffff', border: '1px solid #e9ecef', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}>
                    {activeSessionId ? renderActiveChat() : renderThreadsList()}
                </div>
            ) : (
                // DESKTOP 2-COLUMN VIEW:
                <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 0, height: '680px', background: '#ffffff', border: '1px solid #e9ecef', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                    <div style={{ borderRight: rtl ? 'none' : '1px solid #e9ecef', borderLeft: rtl ? '1px solid #e9ecef' : 'none', height: '100%', minHeight: 0 }}>
                        {renderThreadsList()}
                    </div>
                    <div style={{ height: '100%', minHeight: 0 }}>
                        {renderActiveChat()}
                    </div>
                </div>
            )}

            {/* Fullscreen Photo Lightbox Modal (WhatsApp Standard) */}
            {selectedImageModal && (
                <div 
                    onClick={() => setSelectedImageModal(null)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 99999,
                        background: 'rgba(0, 0, 0, 0.92)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 16
                    }}
                >
                    <div style={{ position: 'absolute', top: 20, right: 20, display: 'flex', gap: 12, zIndex: 100000 }}>
                        <a
                            href={selectedImageModal}
                            download="luxvono-image"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.15)',
                                border: '1px solid rgba(255, 255, 255, 0.25)',
                                color: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                textDecoration: 'none'
                            }}
                            title={rtl ? 'تحميل الصورة' : 'Download Image'}
                        >
                            <Download size={20} />
                        </a>
                        <button
                            type="button"
                            onClick={() => setSelectedImageModal(null)}
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.15)',
                                border: '1px solid rgba(255, 255, 255, 0.25)',
                                color: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer'
                            }}
                            title={rtl ? 'إغلاق' : 'Close'}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <img
                        src={selectedImageModal}
                        alt="Preview"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: '92vw',
                            maxHeight: '88vh',
                            borderRadius: '12px',
                            objectFit: 'contain',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.7)'
                        }}
                    />
                </div>
            )}

            {/* WhatsApp Voice & Video Calling Screen Overlay */}
            {activeCall && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 99999,
                        background: 'linear-gradient(180deg, #0b141a 0%, #111b21 50%, #0c1317 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '40px 24px max(32px, env(safe-area-inset-bottom)) 24px',
                        color: '#ffffff',
                        textAlign: 'center'
                    }}
                >
                    {/* Top Security & Call Mode */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.08)', padding: '5px 14px', borderRadius: 20, fontSize: 11, color: '#8696a0' }}>
                            <span>🔒</span>
                            <span>{rtl ? 'مكالمة مشفرة تماماً من طرف لطرف' : 'End-to-end encrypted'}</span>
                        </div>
                        <span style={{ fontSize: 13, color: '#00a884', fontWeight: 700 }}>
                            {activeCall.type === 'video' ? (rtl ? 'مكالمة فيديو LUXVONO' : 'LUXVONO Video Call') : (rtl ? 'مكالمة صوتية LUXVONO' : 'LUXVONO Voice Call')}
                        </span>
                    </div>

                    {/* Center: Video Stream or Calling Ripple Avatar */}
                    {activeCall.type === 'video' ? (
                        <div style={{ position: 'relative', width: '100%', maxWidth: 420, height: '52vh', borderRadius: 20, overflow: 'hidden', background: '#000000', border: '1px solid rgba(255,255,255,0.15)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
                            <video
                                ref={localVideoRef}
                                autoPlay
                                playsInline
                                muted
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', padding: '6px 12px', borderRadius: 12 }}>
                                <span style={{ fontSize: 12, fontWeight: 700 }}>{activeCall.customerName}</span>
                                <span style={{ fontSize: 11, color: '#25d366' }}>
                                    {activeCall.status === 'calling' ? (rtl ? 'جاري الرنين...' : 'Ringing...') : `${Math.floor(callDuration / 60)}:${(callDuration % 60).toString().padStart(2, '0')}`}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
                            <div style={{ position: 'relative', width: 110, height: 110 }}>
                                <div style={{ position: 'absolute', inset: -15, borderRadius: '50%', background: 'rgba(0, 168, 132, 0.25)', animation: 'callWavePulse 2s infinite ease-out' }} />
                                <div style={{ width: 110, height: 110, borderRadius: '50%', background: '#202c33', border: '3px solid #00a884', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38, fontWeight: 800, color: '#ffffff', overflow: 'hidden', position: 'relative', zIndex: 2 }}>
                                    {activeCall.customerAvatar ? (
                                        <img src={activeCall.customerAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (activeCall.customerName || 'C').charAt(0).toUpperCase()}
                                </div>
                            </div>

                            <div>
                                <h2 style={{ margin: '0 0 6px 0', fontSize: 24, fontWeight: 800, color: '#e9edef' }}>
                                    {activeCall.customerName}
                                </h2>
                                <p style={{ margin: 0, fontSize: 14, color: activeCall.status === 'calling' ? '#8696a0' : '#00a884', fontWeight: 600 }}>
                                    {activeCall.status === 'calling' 
                                        ? (rtl ? 'جاري الاتصال والرنين...' : 'Ringing...') 
                                        : `${Math.floor(callDuration / 60)}:${(callDuration % 60).toString().padStart(2, '0')}`}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Bottom WhatsApp Controls Floating Bar */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, background: 'rgba(32, 44, 51, 0.85)', backdropFilter: 'blur(10px)', padding: '14px 28px', borderRadius: 40, border: '1px solid rgba(255,255,255,0.08)' }}>
                        {/* Mute Mic Button */}
                        <button
                            type="button"
                            onClick={toggleCallMute}
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                background: isCallMuted ? '#ef4444' : 'rgba(255,255,255,0.12)',
                                border: 'none',
                                color: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer'
                            }}
                            title={isCallMuted ? (rtl ? 'إلغاء كتم الصوت' : 'Unmute') : (rtl ? 'كتم الصوت' : 'Mute')}
                        >
                            {isCallMuted ? <MicOff size={22} /> : <Mic size={22} />}
                        </button>

                        {/* Video Camera Toggle */}
                        {activeCall.type === 'video' && (
                            <button
                                type="button"
                                onClick={toggleCallVideo}
                                style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    background: isCallVideoOff ? '#ef4444' : 'rgba(255,255,255,0.12)',
                                    border: 'none',
                                    color: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                                title={isCallVideoOff ? (rtl ? 'تشغيل الكاميرا' : 'Camera On') : (rtl ? 'إيقاف الكاميرا' : 'Camera Off')}
                            >
                                <Video size={22} />
                            </button>
                        )}

                        {/* RED End Call Button */}
                        <button
                            type="button"
                            onClick={endCall}
                            style={{
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                background: '#ea0038',
                                border: 'none',
                                color: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 4px 18px rgba(234, 0, 56, 0.45)'
                            }}
                            title={rtl ? 'إنهاء المكالمة' : 'End Call'}
                        >
                            <PhoneOff size={24} />
                        </button>
                    </div>
                </div>
            )}

            {/* Mute Modal */}
            {showMuteModal && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 360, padding: 22, boxShadow: '0 8px 30px rgba(0,0,0,0.15)', textAlign: 'center' }}>
                        <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#fffbeb', border: '1px solid #fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px auto' }}>
                            <VolumeX size={24} color="#d97706" />
                        </div>
                        <h3 style={{ margin: '0 0 6px 0', fontSize: 16, fontWeight: 800, color: '#111827' }}>
                            {rtl ? 'كتم إشعارات المحادثة' : 'Mute Conversation'}
                        </h3>
                        <p style={{ margin: '0 0 18px 0', fontSize: 12.5, color: '#6b7280', lineHeight: 1.5 }}>
                            {rtl 
                                ? `لن تتلقى إشعارات جديدة عند وصول رسائل من "${activeThread?.customerName}".` 
                                : `You will not receive notifications for new messages from this customer.`}
                        </p>

                        <div style={{ display: 'flex', gap: 10 }}>
                            <button
                                type="button"
                                onClick={() => setShowMuteModal(false)}
                                style={{ flex: 1, padding: '9px 0', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}
                            >
                                {rtl ? 'إلغاء' : 'Cancel'}
                            </button>
                            <button
                                type="button"
                                onClick={handleMuteSession}
                                style={{ flex: 1, padding: '9px 0', background: '#d97706', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#ffffff', cursor: 'pointer' }}
                            >
                                {rtl ? 'تأكيد الكتم' : 'Mute'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Safety Modals */}
            {showReportModal && (
                <ReportModal
                    isOpen={showReportModal}
                    onClose={() => setShowReportModal(false)}
                    targetType="CUSTOMER"
                    targetId={activeThread?.customerId}
                />
            )}

            {/* Product Share Modal */}
            <ProductShareModal
                isOpen={showProductShareModal}
                onClose={() => setShowProductShareModal(false)}
                onShareProduct={handleSendProductMessage}
            />
        </div>
    );
}
